import { useQuery } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  FileSpreadsheet,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Wrench,
  Phone,
  Download,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function today() {
  return new Date().toISOString().split('T')[0];
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone edge cases
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-600',
  no_response: 'bg-orange-100 text-orange-800',
  no_phone: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace('_', ' ') || '—'}
    </span>
  );
}

function ReminderStatusIcon({ info, label }) {
  if (!info) return <span className="text-gray-300 text-[11px]">—</span>;
  const isSent = info.status === 'sent';
  const isFailed = info.status === 'failed';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${isSent ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-yellow-600'}`}>
      {isSent ? <CheckCircle2 className="w-3.5 h-3.5" /> : isFailed ? <XCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
      {isSent ? 'Sent' : isFailed ? 'Failed' : 'Pending'}
    </span>
  );
}

function SummaryCard({ label, value, subtext, color = 'text-brand-dark', bgColor = 'bg-white' }) {
  return (
    <div className={`${bgColor} rounded-xl border border-brand-light/80 p-3 text-center`}>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[11px] text-brand-neutral font-medium">{label}</p>
      {subtext && <p className="text-[10px] text-brand-neutral/70 mt-0.5">{subtext}</p>}
    </div>
  );
}

/** Generate CSV and trigger download */
function downloadExcel(rows, date) {
  // One header per value pushed below. The last five were missing, so every
  // export shifted those columns under no heading at all.
  const headers = [
    'WO#', 'Customer', 'Phone', 'Collection Address', 'Job Category', 'Asset',
    'Serial Number', 'Status', 'Slot', 'Position', 'Mechanic',
    'Booked Via', 'Booked At',
    'Booking Link Sent', 'Link Send Count', 'Link Last Sent',
    'Day-Before Reminder', 'Same-Day Reminder',
  ];

  const csvRows = [headers.join(',')];

  for (const r of rows) {
    const row = [
      r.workOrderNumber || '',
      `"${(r.customerName || '').replace(/"/g, '""')}"`,
      r.customerPhone || '',
      `"${(r.collectionAddress || '').replace(/"/g, '""')}"`,
      r.jobCategory || '',
      `"${(r.assetName || '').replace(/"/g, '""')}"`,
      r.assetSerialNumber || '',
      r.status || '',
      r.slotLabel || '',
      r.buggyPosition ?? '',
      r.mechanicName || '',
      `"${(r.bookedVia || '').replace(/"/g, '""')}"`,
      r.scheduledAt || '',
      r.bookingLinkSent ? 'Yes' : 'No',
      r.bookingLinkSendCount || 0,
      r.bookingLinkLastSent || '',
      r.dayBeforeReminder?.status || 'N/A',
      r.sameDayReminder?.status || 'N/A',
    ];
    csvRows.push(row.join(','));
  }

  const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daily-report-${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DailyReport() {
  const { selectedLocationId } = useLocationContext();
  const [date, setDate] = useState(today());

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['daily-report', date, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/reports/daily', { params: { date } });
      return data;
    },
  });

  const filteredRows = data?.rows || [];

  const summary = data?.summary;

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-brand-dark flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-brand-primary" />
                Scheduled Jobs
              </h1>
              <p className="text-sm text-brand-neutral mt-0.5">
                Daily view of all scheduled AMC jobs
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-1.5 px-3 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {data?.rows?.length > 0 && (
                <button
                  onClick={() => downloadExcel(data.rows, date)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              )}
            </div>
          </div>

          {/* Date Selector */}
          <div className="bg-white rounded-xl border border-brand-light/80 p-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDate(shiftDate(date, -1))}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-brand-light/60 transition-colors shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-neutral shrink-0" />
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-primary/30 w-40 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute"
                />
              </div>
              <button
                type="button"
                onClick={() => { setDate(shiftDate(date, 1)); }}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-brand-light/60 transition-colors shrink-0"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDate(today())}
                className="px-3 py-1.5 text-[12px] font-medium text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary/5 transition-colors"
              >
                Today
              </button>
              <span className="text-sm font-medium text-brand-dark ml-2">{formatDate(date)}</span>
            </div>
          </div>

          {/* Summary Cards */}
          {summary && !isLoading && (
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="Total Jobs" value={summary.totalJobs} />
              <SummaryCard label="Scheduled" value={summary.scheduled} color="text-green-600" bgColor="bg-green-50" />
              <SummaryCard label="TBC" value={summary.tbc} color="text-amber-600" bgColor="bg-amber-50" />
            </div>
          )}


          {/* Table */}
          {isLoading ? (
            <div className="text-center py-16">
              <RefreshCw className="w-8 h-8 text-brand-primary animate-spin mx-auto mb-3" />
              <p className="text-sm text-brand-neutral">Loading report...</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-brand-light/80">
              <FileSpreadsheet className="w-12 h-12 text-brand-neutral/30 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-brand-dark mb-1">No Jobs Found</h3>
              <p className="text-sm text-brand-neutral">No jobs scheduled for {formatDate(date)}.</p>
            </div>
          ) : (
            <>
              {/* Mobile Cards */}
              <div className="sm:hidden space-y-3">
                {filteredRows.map(row => (
                  <MobileReportCard key={row.id} row={row} />
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-brand-light/80 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-brand-light/40 text-[11px] font-semibold text-brand-neutral uppercase tracking-wide">
                        <th className="px-3 py-2.5 text-left">WO#</th>
                        <th className="px-3 py-2.5 text-left">Customer</th>
                        <th className="px-3 py-2.5 text-left">Collection Address</th>
                        <th className="px-3 py-2.5 text-left">Asset</th>
                        <th className="px-3 py-2.5 text-left">Status</th>
                        <th className="px-3 py-2.5 text-left">Slot</th>
                        <th className="px-3 py-2.5 text-left">Mechanic</th>
                        <th className="px-3 py-2.5 text-left">Booked Via</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-light/60">
                      {filteredRows.map(row => (
                        <tr key={row.id} className="hover:bg-brand-light/20 transition-colors">
                          <td className="px-3 py-2.5 font-mono text-[12px] text-brand-neutral">
                            {row.workOrderNumber || '—'}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-brand-dark text-[13px]">{row.customerName}</div>
                            {row.customerPhone && (
                              <div className="flex items-center gap-1 text-[11px] text-brand-neutral">
                                <Phone className="w-3 h-3" />{row.customerPhone}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-[12px] text-brand-dark max-w-[220px]">
                            {row.collectionAddress || <span className="text-brand-neutral">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="text-[12px]">{row.assetName || '—'}</div>
                            {row.assetSerialNumber && (
                              <div className="text-[11px] text-brand-neutral">{row.assetSerialNumber}</div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="px-3 py-2.5 text-[12px]">
                            {row.slotLabel || '—'}
                            {row.buggyPosition != null && (
                              <span className="ml-1 text-[10px] bg-brand-light/60 px-1 py-0.5 rounded">P{row.buggyPosition}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {row.mechanicName ? (
                              <span className="flex items-center gap-1 text-[12px]">
                                <Wrench className="w-3 h-3 text-brand-neutral" />{row.mechanicName}
                              </span>
                            ) : (
                              <span className="text-[11px] text-gray-300">Unassigned</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-[11px] font-medium">{row.bookedVia}</span>
                            {row.scheduledAt && (
                              <div className="text-[10px] text-brand-neutral">{formatDateTime(row.scheduledAt)}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

function MobileReportCard({ row }) {
  return (
    <div className="bg-white rounded-xl border border-brand-light/80 p-4 space-y-2">
      <div className="flex items-start justify-between">
        <div>
          <span className="font-semibold text-brand-dark text-[14px]">{row.customerName}</span>
          {row.workOrderNumber && (
            <span className="ml-2 text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-mono">
              WO# {row.workOrderNumber}
            </span>
          )}
        </div>
        <StatusBadge status={row.status} />
      </div>

      {row.assetName && (
        <p className="text-xs text-brand-neutral">
          {row.assetName}{row.assetSerialNumber ? ` (${row.assetSerialNumber})` : ''}
        </p>
      )}

      <div className="flex flex-wrap gap-2 text-[11px] text-brand-neutral">
        {row.mechanicName && (
          <span className="flex items-center gap-1"><Wrench className="w-3 h-3" /> {row.mechanicName}</span>
        )}
        {row.slotLabel && (
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {row.slotLabel}</span>
        )}
        {row.buggyPosition != null && (
          <span className="bg-brand-light/60 px-1.5 py-0.5 rounded">Pos {row.buggyPosition}</span>
        )}
      </div>

      <div className="flex items-center gap-1 text-[11px]">
        <span className="text-brand-neutral font-medium">Booked via:</span>
        <span className="font-semibold text-brand-dark">{row.bookedVia}</span>
      </div>

    </div>
  );
}
